import { useState, useMemo, useCallback, useEffect } from 'react'
import useSWR, { SWRConfiguration, mutate } from 'swr'
import { toast } from 'react-toastify'
import { planningApi } from '../services/api'
import {
  IDiscipline,
  IDisciplineSelection,
  IDisciplineApiResponse,
  TKnowledgeLevel,
  TRelevanceLevel,
} from '../types'

interface UseDisciplinesParams {
  contestId: string | null
}

export const useDisciplines = (params: UseDisciplinesParams) => {
  const { contestId } = params
  const [disciplineSelections, setDisciplineSelections] = useState<IDisciplineSelection[]>([])

  // Generate cache key based on contestId
  const cacheKey = contestId ? `/study-plan/disciplines?courseId=${contestId}` : null

  const swr = useSWR<IDisciplineApiResponse>(cacheKey, fetcher, configuration)

  // Update local state when new data arrives
  useEffect(() => {
    if (swr.data && swr.data.length > 0) {
      const transformedDisciplines = swr.data.map((discipline) => ({
        ...discipline,
        knowledgeLevel: 0 as TKnowledgeLevel,
        relevanceLevel: 1 as TRelevanceLevel,
        isSelected: false,
        weight: 0,
      }))
      setDisciplineSelections(transformedDisciplines)
    }
  }, [swr.data])

  // Memoized data transformation
  const disciplines = useMemo(() => {
    return disciplineSelections
  }, [disciplineSelections])

  // Refresh disciplines data
  const refreshDisciplines = useCallback(() => {
    if (cacheKey) {
      mutate(cacheKey)
    }
  }, [cacheKey])

  // NOTE: Removed the useEffect that resets disciplines when contestId changes
  // It was causing a race condition where cached SWR data would be set and immediately cleared
  // SWR already handles cache key changes automatically

  return {
    disciplines,
    loading: swr.isLoading,
    error: swr.error,
    refreshDisciplines,
    // Legacy API compatibility
    refetch: refreshDisciplines,
  }
}

// SWR fetcher function
const fetcher = (url: string) => {
  const urlObj = new URL(url, window.location.origin)
  const courseId = urlObj.searchParams.get('courseId')

  if (!courseId) {
    throw new Error('Course ID is required')
  }

  return planningApi.getDisciplines(courseId)
}

// SWR configuration
const configuration: SWRConfiguration = {
  fallbackData: undefined,
  shouldRetryOnError: true,
  onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
    if (retryCount > 3) {
      toast.error('Não foi possível carregar as disciplinas. Tente novamente mais tarde.')
    } else {
      setTimeout(() => revalidate({ retryCount }), 1000 * Math.pow(2, retryCount))
    }
  },
  dedupingInterval: 60e3, // 1 minute
  revalidateOnReconnect: true,
  revalidateOnFocus: false,
  refreshInterval: 10 * 60 * 1000, // 10 minutes cache invalidation
  errorRetryCount: 3,
  onError: (error) => {
    console.error('SWR Error fetching disciplines:', error)
    toast.error('Erro ao carregar disciplinas. Tente novamente.')
  },
}
